import { Accordion, Button, Input, InputPassword, Text } from '@kubit-ui-web/react-components';
import type { CompletedStory, Deck, RoomState } from '@point-ker/shared';
import { useState } from 'react';

import { buildJiraCurl } from '@/jira/command';
import {
  isJiraReady,
  loadJiraSettings,
  saveJiraSettings,
  type JiraSettings,
} from '@/jira/settings';
import { ChevronDownIcon } from '@/ui/icons';
import { Card, Hint, Row, Spread, Stack } from '@/ui/layout';

/**
 * What the room has estimated so far, and — for the host — a way to file each
 * result in Jira.
 *
 * The export is a `curl` on the clipboard, never a request from this app. The
 * host is the one with Jira access, the credential is theirs, and a command
 * they can read before running is the only version of this that does not ask
 * a planning-poker room to be trusted with an account-wide token.
 */

/** The accordion is a settings panel inside a card, not a page section. */
const JIRA_ACCORDION_STYLES = {
  container: { background_color: 'transparent' },
  decorative: { background_color: 'transparent', width: '0rem' },
  triggerIconContainer: { background_color: 'transparent', padding: '0rem' },
  titleContainer: { margin_left: '0.5rem', gap: '0.5rem' },
  title: { font_size: 'inherit', font_weight: 'inherit', line_height: 'inherit' },
  content: { padding: '0.75rem 0rem 0rem 0rem' },
};

export function SessionHistory({ state, isHost }: { state: RoomState; isHost: boolean }) {
  const [settings, setSettings] = useState<JiraSettings>(loadJiraSettings);
  // Never persisted, and never sent anywhere: see `jira/settings.ts`.
  const [token, setToken] = useState('');

  if (state.history.length === 0) return null;

  const update = (patch: Partial<JiraSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveJiraSettings(next);
  };

  const ready = isJiraReady(settings, token);

  return (
    <Card>
      <Text variant="HEADING_H3_EXPANDED" component="h2">
        Estimated this session
      </Text>

      <Stack $gap="0.5rem">
        {state.history.map((entry) => (
          <HistoryRow
            key={entry.story.id}
            entry={entry}
            deck={state.deck}
            settings={settings}
            token={token}
            canExport={isHost && ready}
            showExport={isHost}
          />
        ))}
      </Stack>

      <Text variant="PARAGRAPH_CAPTION_EXPANDED" component="span">
        {state.history.length} stor{state.history.length === 1 ? 'y' : 'ies'} estimated. This list
        lives in the server’s memory and is lost if the room expires.
      </Text>

      {isHost && (
        <Accordion
          variant="DEFAULT"
          defaultOpen={false}
          hasHeaderLineSeparator={false}
          ctv={JIRA_ACCORDION_STYLES}
          triggerComponent="div"
          triggerIcon={{ icon: <ChevronDownIcon /> }}
          title={{
            component: 'div',
            content: (
              <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
                {ready ? 'Jira export' : 'Jira export — not set up'}
              </Text>
            ),
          }}
        >
          <JiraSettingsForm
            settings={settings}
            token={token}
            onChange={update}
            onTokenChange={setToken}
          />
        </Accordion>
      )}
    </Card>
  );
}

function HistoryRow({
  entry,
  deck,
  settings,
  token,
  canExport,
  showExport,
}: {
  entry: CompletedStory;
  deck: Deck;
  settings: JiraSettings;
  token: string;
  canExport: boolean;
  showExport: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const command = buildJiraCurl({
      settings,
      token,
      ticket: entry.story.title,
      estimate: entry.estimate,
      deck,
    });
    // Clipboard access can be denied or unavailable over plain http. There is
    // nothing else on screen to fall back to here, so a failure just leaves
    // the button unconfirmed rather than claiming a copy that did not happen.
    void navigator.clipboard
      ?.writeText(command)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <Spread $gap="0.5rem">
      <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
        {entry.story.title}
      </Text>
      <Row $gap="0.5rem" $width="fit-content">
        <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="span">
          {entry.estimate}
        </Text>
        {showExport && (
          <Button
            variant="SECONDARY"
            size="SMALL"
            disabled={!canExport}
            title={canExport ? 'Copy a curl that files this estimate in Jira' : undefined}
            onClick={copy}
          >
            {copied ? 'Copied' : 'Copy curl'}
          </Button>
        )}
      </Row>
    </Spread>
  );
}

function JiraSettingsForm({
  settings,
  token,
  onChange,
  onTokenChange,
}: {
  settings: JiraSettings;
  token: string;
  onChange: (patch: Partial<JiraSettings>) => void;
  onTokenChange: (token: string) => void;
}) {
  return (
    <Stack $gap="0.75rem">
      <Hint>
        Fills the command the copy buttons put on your clipboard. Nothing is sent from here — run
        it in a terminal that reaches your Jira.
      </Hint>

      <Input
        variant="DEFAULT"
        id="jira-email"
        label={{ content: 'Atlassian email' }}
        placeholder="ada@example.com"
        value={settings.email}
        maxLength={120}
        onChange={(event) => onChange({ email: event.target.value })}
      />
      <Input
        variant="DEFAULT"
        id="jira-site"
        label={{ content: 'Jira site' }}
        placeholder="acme"
        value={settings.site}
        maxLength={120}
        onChange={(event) => onChange({ site: event.target.value })}
      />
      <Hint>{`acme, acme.atlassian.net and https://acme.atlassian.net/ all work.`}</Hint>

      <Input
        variant="DEFAULT"
        id="jira-field"
        label={{ content: 'Estimate field' }}
        placeholder="customfield_10035"
        value={settings.customField}
        maxLength={60}
        onChange={(event) => onChange({ customField: event.target.value })}
      />

      <InputPassword
        variant="DEFAULT"
        id="jira-token"
        label={{ content: 'API token' }}
        value={token}
        maxLength={512}
        onChange={(event) => onTokenChange(event.target.value)}
      />
      <Hint>
        Kept in this tab only and retyped after a refresh. The other fields are remembered; a token
        that reaches your whole Jira account is not something to leave on disk.
      </Hint>
    </Stack>
  );
}
